import React, { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { UvProjection } from '../types'
import { Field, Select, Slider } from './ui'

interface FileUploadBoxProps {
  onFileLoad: (files: File[]) => void
  deflection: number
  setDeflection: (val: number) => void
  uvProjection: UvProjection
  setUvProjection: (val: UvProjection) => void
}

export const FileUploadBox: React.FC<FileUploadBoxProps> = ({
  onFileLoad,
  deflection,
  setDeflection,
  uvProjection,
  setUvProjection,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const take = (files: FileList | null) => {
    if (files && files.length > 0) onFileLoad(Array.from(files))
  }

  return (
    <div className="flex flex-col items-center justify-center h-full w-full bg-gray-50 p-lg overflow-y-auto">
      <div className="w-full max-w-[520px] bg-white border border-gray-200 rounded-lg shadow-box p-lg">
        <h2 className="text-title-lg text-gray-900">Start a model</h2>
        <p className="text-text-md text-gray-500 mt-xs mb-md">
          Bring in a CAD or mesh file to colour, name its parts, place a logo and export a GLB.
        </p>

        {/* Drop zone — the portal's dashed "file" button, enlarged. */}
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            take(e.dataTransfer.files)
          }}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-xs rounded-md border border-dashed py-xl px-md cursor-pointer transition-all
            ${dragging ? 'border-primary-500 bg-primary-25' : 'border-gray-300 bg-gray-25 hover:border-primary-500'}`}
        >
          <Upload size={20} className="text-primary-600" />
          <span className="text-title-md text-gray-900">Choose a file or drop it here</span>
          <span className="text-text-sm text-gray-500">STEP, STP, STL, GLB or GLTF</span>
          <input
            ref={inputRef}
            type="file"
            accept=".step,.stp,.stl,.glb,.gltf"
            multiple
            className="hidden"
            onChange={(e) => take(e.target.files)}
          />
        </div>

        <div className="mt-md pt-md border-t border-gray-200">
          <Field
            label={
              <>
                Smoothness quality <span className="text-gray-400">(STEP only)</span>
              </>
            }
            value={deflection.toFixed(4)}
          >
            <Slider
              min="0"
              max="1"
              step="0.01"
              defaultValue="0.36"
              onChange={(e) => setDeflection(Math.pow(10, parseFloat(e.target.value) * 3 - 3))}
            />
            <div className="flex justify-between text-text-xs text-gray-400 mt-xs">
              <span>Ultra smooth</span>
              <span>Coarse</span>
            </div>
          </Field>

          <Field label="UV projection" value={uvProjection} hint="Generates TEXCOORD_0 for texture and shader mapping.">
            <Select value={uvProjection} onChange={(e) => setUvProjection(e.target.value as UvProjection)}>
              <option value="cylindrical">Cylindrical — cups and bottles</option>
              <option value="box">Box — general shapes</option>
              <option value="planar">Planar — flat objects</option>
              <option value="none">None — keep the GLB&apos;s own UVs</option>
            </Select>
          </Field>
        </div>
      </div>
    </div>
  )
}
